/**
 * Email Templates for Form Submissions
 * Custom email content for each form type with relevant data
 * Comprehensive update - all fields mapped from actual form components
 */

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

// Helper to format Yes/No checkbox fields into a list
const getSelectedCheckboxes = (data, checkboxFields) => {
  const selected = [];
  checkboxFields.forEach(field => {
    if (data[field.name] === 'Yes' || data[field.name] === true) {
      selected.push(field.label);
    }
  });
  return selected;
};

// Helper to format array as list
const formatList = (items) => {
  if (!items || (Array.isArray(items) && items.length === 0)) return 'None selected';
  if (Array.isArray(items)) return items.join(', ');
  return items;
};

// Helper to format boolean/Yes/No values
const formatYesNo = (value) => {
  if (value === true || value === 'Yes') return 'Yes';
  if (value === false || value === 'No') return 'No';
  return value || 'N/A';
};

// Common email header
const emailHeader = (title, color = '#4f46e5') => `
  <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto;">
    <div style="background-color: ${color}; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
      <h1 style="color: white; margin: 0; font-size: 24px;">${title}</h1>
    </div>
`;

// Common email footer
const emailFooter = (practice, userEmail, submittedAt) => `
    <div style="background-color: #f3f4f6; padding: 15px; margin-top: 20px; border-radius: 5px;">
      <p style="margin: 5px 0; color: #6b7280; font-size: 14px;"><strong>Practice:</strong> ${practice || 'N/A'}</p>
      <p style="margin: 5px 0; color: #6b7280; font-size: 14px;"><strong>Submitted By:</strong> ${userEmail || 'N/A'}</p>
      <p style="margin: 5px 0; color: #6b7280; font-size: 14px;"><strong>Date:</strong> ${formatDate(submittedAt)}</p>
    </div>
    <div style="text-align: center; padding: 20px; color: #9ca3af; font-size: 12px;">
      <p>This is an automated notification from AlliedOMS Quality Improvement System.</p>
      <p>Please log in to view complete details and take appropriate action.</p>
    </div>
  </div>
`;

// Section helper
const section = (title, content, bgColor = '#f9fafb') => `
  <div style="background-color: ${bgColor}; padding: 15px; margin: 15px 0; border-radius: 5px; border-left: 4px solid #4f46e5;">
    <h3 style="margin: 0 0 10px 0; color: #1f2937; font-size: 16px;">${title}</h3>
    ${content}
  </div>
`;

// Row helper for consistent field display
const row = (label, value) => `<p style="margin: 5px 0;"><strong>${label}:</strong> ${value || 'N/A'}</p>`;

// ============================================
// COMPLICATION FORM TEMPLATES
// ============================================

export const generateAnesthesiaEmail = (data) => {
  // Track selected medications with doses
  const selectedMeds = [];
  
  if (Array.isArray(data.medications)) {
    data.medications.forEach(med => {
      const dose = data[`${med}Dose`];
      if (dose) {
        selectedMeds.push(`${med}: ${dose}`);
      } else {
        selectedMeds.push(med);
      }
    });
    // Handle "Other" medication
    if (data.medications.includes('Other') && data.otherMedication) {
      const otherDose = data.otherDose ? `: ${data.otherDose}` : '';
      selectedMeds.push(`Other (${data.otherMedication})${otherDose}`);
    }
  }
  
  // Co-morbidities
  const coMorbiditiesList = Array.isArray(data.coMorbidities) ? data.coMorbidities.join(', ') : (data.coMorbidities || 'None');
  const coMorbiditiesWithOther = data.coMorbiditiesOther ? `${coMorbiditiesList} (Other: ${data.coMorbiditiesOther})` : coMorbiditiesList;
  
  // Intra/post operative events
  const eventCategories = [
    { name: 'intraPostCriticalEvents', label: 'Critical Events' },
    { name: 'intraPostAirwayPulmonaryEvents', label: 'Airway/Pulmonary Events' },
    { name: 'intraPostCardiacEvents', label: 'Cardiac Events' },
    { name: 'intraPostMedicationTransfusionEvents', label: 'Medication/Transfusion Events' },
    { name: 'intraPostNeurologicalOtherEvents', label: 'Neurological/Other Events' },
    { name: 'intraPostEquipmentEnvironmentEvents', label: 'Equipment/Environment Events' },
    { name: 'intraPostProcessEvents', label: 'Process Events' },
    { name: 'intraPostOtherEvents', label: 'Other Events' }
  ];
  
  const allEvents = [];
  const eventsByCategory = {};
  eventCategories.forEach(cat => {
    if (data[cat.name] && Array.isArray(data[cat.name]) && data[cat.name].length > 0) {
      eventsByCategory[cat.label] = data[cat.name];
      data[cat.name].forEach(event => {
        allEvents.push(`${cat.label}: ${event}`);
      });
    }
  });
  
  const html = `
    ${emailHeader('⚠️ Anesthesia Complication Report', '#dc2626')}
    <div style="padding: 20px;">
      ${section('Patient Demographics', `
        ${row('Age', data.age ? `${data.age} years` : null)}
        ${row('Weight', data.weight ? `${data.weight} lb` : null)}
        ${row('Sex', data.sex)}
        ${row('Date of Surgery', formatDate(data.dateOfSurgery))}
      `)}
      
      ${section('Anesthesia Classification', `
        ${row('ASA Classification', data.americanSocietyAnesthesiologistsClassification)}
      `)}
      
      ${section('Anesthesia Details', `
        ${row('Anesthesia Type', data.anesthesiaType)}
        ${row('Anesthesia Provider', data.anesthesiaProvider)}
        ${row('Surgical Time', data.surgicalTime ? `${data.surgicalTime} minutes` : null)}
        ${row('Primary Procedure', data.primaryProcedure)}
      `)}
      
      ${section('Co-Morbidities', `
        <p style="margin: 5px 0;">${coMorbiditiesWithOther}</p>
      `)}
      
      ${selectedMeds.length > 0 ? section('Medications Administered', `
        <ul style="margin: 0; padding-left: 20px;">
          ${selectedMeds.map(med => `<li style="margin: 5px 0;">${med}</li>`).join('')}
        </ul>
      `) : ''}
      
      ${data.intraPostOperativeEvents && allEvents.length > 0 ? section('Intra/Post-Operative Events', `
        ${Object.entries(eventsByCategory).map(([category, events]) => `
          <p style="margin: 10px 0 5px 0; font-weight: bold; color: #dc2626;">${category}:</p>
          <ul style="margin: 0; padding-left: 20px;">
            ${events.map(e => `<li style="margin: 3px 0;">${e}</li>`).join('')}
          </ul>
        `).join('')}
      `, '#fef2f2') : section('Intra/Post-Operative Events', `<p style="margin: 0;">No events reported</p>`)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `ANESTHESIA COMPLICATION REPORT

PATIENT DEMOGRAPHICS
Age: ${data.age || 'N/A'} years
Weight: ${data.weight || 'N/A'} lb
Sex: ${data.sex || 'N/A'}
Date of Surgery: ${formatDate(data.dateOfSurgery)}

ANESTHESIA CLASSIFICATION
ASA Classification: ${data.americanSocietyAnesthesiologistsClassification || 'N/A'}

ANESTHESIA DETAILS
Anesthesia Type: ${data.anesthesiaType || 'N/A'}
Anesthesia Provider: ${data.anesthesiaProvider || 'N/A'}
Surgical Time: ${data.surgicalTime ? `${data.surgicalTime} minutes` : 'N/A'}
Primary Procedure: ${data.primaryProcedure || 'N/A'}

CO-MORBIDITIES
${coMorbiditiesWithOther}

MEDICATIONS ADMINISTERED
${selectedMeds.length > 0 ? selectedMeds.join('\n') : 'None recorded'}

INTRA/POST-OPERATIVE EVENTS
${allEvents.length > 0 ? allEvents.join('\n') : 'No events reported'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `⚠️ Anesthesia Complication Report - ${data.practice || 'Unknown Practice'}` };
};

export const generateDentalImplantEmail = (data) => {
  // Format manufacturer with "Other" option
  const manufacturerDisplay = data.manufacturer === 'Other' && data.manufacturerOther 
    ? `Other: ${data.manufacturerOther}` 
    : data.manufacturer;
  
  // Format arrays
  const tissueFeaturesList = formatList(data.tissueFeatures);
  const graftingTypeList = formatList(data.graftingType);
  const healingList = formatList(data.healing);
  const restoringPractitionerList = formatList(data.restoringPractitioner);
  const surgeonIssuesList = formatList(data.surgeonIssues);
  const deviceIssuesList = formatList(data.deviceIssues);
  const diagnosisList = formatList(data.diagnosisAtFailure);
  const insertionTorqueList = formatList(data.insertionTorque);
  const contextList = formatList(data.contextOfPlacement);
  
  const html = `
    ${emailHeader('🦷 Dental Implant Complication Report', '#dc2626')}
    <div style="padding: 20px;">
      ${section('Implant Manufacturer & Product Info', `
        ${row('Manufacturer', manufacturerDisplay)}
        ${row('Item Number', data.itemNumber)}
        ${row('Serial Number', data.serialNumber)}
        ${row('Lot Number', data.lotNumber)}
      `)}
      
      ${section('Timing & Failure', `
        ${row('Insertion Date', formatDate(data.insertionDate))}
        ${row('Time to Failure', data.timeToFailure)}
      `)}
      
      ${section('Placement Details', `
        ${row('Insertion Torque', insertionTorqueList)}
        ${row('Context of Placement', contextList)}
        ${row('Implant Site Tissue Features', tissueFeaturesList)}
      `)}
      
      ${section('Grafting Information', `
        ${row('Type of Graft', graftingTypeList)}
        ${row('Membrane', data.membrane)}
        ${data.membrane === 'Yes' ? row('Membrane Type', data.membraneType) : ''}
        ${row('Healing', healingList)}
      `)}
      
      ${section('Patient Information', `
        ${row('Age', data.age)}
        ${row('Medical History', data.medicalHistory)}
        ${row('Surgical History', data.surgicalHistory)}
        ${row('Radiation', data.radiation)}
        ${row('Chemotherapy', data.chemotherapy)}
        ${row('Medication', data.medication)}
        ${row('Tobacco Use', data.tobaccoUse)}
        ${row('Alcohol Use', data.alcoholUse)}
        ${row('Recreational Drug Use', data.recreationalDrug)}
        ${row('Hygiene', data.hygiene)}
        ${row('Parafunctional Habits', data.parafunctionalHabits)}
        ${row('Periodontitis', data.periodontitis)}
        ${row('Gingivitis', data.gingivitis)}
      `)}
      
      ${section('Restoring Practitioner Issues', `
        ${row('Issues', restoringPractitionerList)}
        ${data.prostheticRestoration ? row('Prosthetic Restoration', data.prostheticRestoration) : ''}
      `)}
      
      ${section('Surgeon Issues', `
        <p style="margin: 5px 0;">${surgeonIssuesList}</p>
      `)}
      
      ${section('Device Issues', `
        <p style="margin: 5px 0;">${deviceIssuesList}</p>
      `)}
      
      ${section('Surgical Instrumentation', `
        ${row('Sterilization Method', data.sterilizationMethod)}
        ${row('Cycles Before Drill Replacement', data.cyclesBeforeReplacement)}
      `)}
      
      ${section('Diagnosis at Failure', `
        <p style="margin: 5px 0;">${diagnosisList}</p>
      `, '#fef2f2')}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `DENTAL IMPLANT COMPLICATION REPORT

IMPLANT MANUFACTURER & PRODUCT INFO
Manufacturer: ${manufacturerDisplay || 'N/A'}
Item Number: ${data.itemNumber || 'N/A'}
Serial Number: ${data.serialNumber || 'N/A'}
Lot Number: ${data.lotNumber || 'N/A'}

TIMING & FAILURE
Insertion Date: ${formatDate(data.insertionDate)}
Time to Failure: ${data.timeToFailure || 'N/A'}

PLACEMENT DETAILS
Insertion Torque: ${insertionTorqueList}
Context of Placement: ${contextList}
Implant Site Tissue Features: ${tissueFeaturesList}

GRAFTING INFORMATION
Type of Graft: ${graftingTypeList}
Membrane: ${data.membrane || 'N/A'}
${data.membrane === 'Yes' ? `Membrane Type: ${data.membraneType || 'N/A'}` : ''}
Healing: ${healingList}

PATIENT INFORMATION
Age: ${data.age || 'N/A'}
Medical History: ${data.medicalHistory || 'N/A'}
Surgical History: ${data.surgicalHistory || 'N/A'}
Radiation: ${data.radiation || 'N/A'}
Chemotherapy: ${data.chemotherapy || 'N/A'}
Medication: ${data.medication || 'N/A'}
Tobacco Use: ${data.tobaccoUse || 'N/A'}
Alcohol Use: ${data.alcoholUse || 'N/A'}
Recreational Drug Use: ${data.recreationalDrug || 'N/A'}
Hygiene: ${data.hygiene || 'N/A'}
Parafunctional Habits: ${data.parafunctionalHabits || 'N/A'}
Periodontitis: ${data.periodontitis || 'N/A'}
Gingivitis: ${data.gingivitis || 'N/A'}

RESTORING PRACTITIONER ISSUES
${restoringPractitionerList}
${data.prostheticRestoration ? `Prosthetic Restoration: ${data.prostheticRestoration}` : ''}

SURGEON ISSUES
${surgeonIssuesList}

DEVICE ISSUES
${deviceIssuesList}

SURGICAL INSTRUMENTATION
Sterilization Method: ${data.sterilizationMethod || 'N/A'}
Cycles Before Drill Replacement: ${data.cyclesBeforeReplacement || 'N/A'}

DIAGNOSIS AT FAILURE
${diagnosisList}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🦷 Dental Implant Complication - ${data.practice || 'Unknown Practice'}` };
};

export const generateDentoalveolarEmail = (data) => {
  // Dentoalveolar surgery form checkbox fields - exact names from the form
  const complicationFields = [
    { name: 'jawFracture', label: 'Jaw Fracture' },
    { name: 'alveolarOsteitis', label: 'Alveolar Osteitis (Dry Socket)' },
    { name: 'sinusExposure', label: 'Sinus Exposure' },
    { name: 'nerveDamage', label: 'Nerve Damage' },
    { name: 'retainedRootTip', label: 'Retained Root Tip' },
    { name: 'damageAdjacentTeeth', label: 'Damage to Adjacent Teeth' },
    { name: 'lipLaceration', label: 'Lip Laceration' },
    { name: 'lipBurn', label: 'Lip Burn' },
    { name: 'retainedInstrumentForeignBody', label: 'Retained Instrument/Foreign Body' }
  ];
  
  const selectedComplications = getSelectedCheckboxes(data, complicationFields);
  
  const html = `
    ${emailHeader('🔧 Dentoalveolar Surgery Complication', '#dc2626')}
    <div style="padding: 20px;">
      ${selectedComplications.length > 0 ? section('Complications Reported', `
        <ul style="margin: 0; padding-left: 20px;">
          ${selectedComplications.map(c => `<li style="margin: 5px 0; color: #dc2626; font-weight: bold;">${c}</li>`).join('')}
        </ul>
      `, '#fef2f2') : section('Complications', `<p style="margin: 0;">No complications selected</p>`)}
      
      ${data.nerveDamage === 'Yes' && data.nerveDamageType ? section('Nerve Damage Details', `
        ${row('Type', data.nerveDamageType)}
      `) : ''}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `DENTOALVEOLAR SURGERY COMPLICATION

COMPLICATIONS REPORTED
${selectedComplications.length > 0 ? selectedComplications.map(c => `• ${c}`).join('\n') : '• None selected'}

${data.nerveDamage === 'Yes' && data.nerveDamageType ? `NERVE DAMAGE TYPE: ${data.nerveDamageType}\n` : ''}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔧 Dentoalveolar Surgery Complication - ${data.practice || 'Unknown Practice'}` };
};

export const generateNeedleStickEmail = (data) => {
  const html = `
    ${emailHeader('💉 Needle Stick Injury Report', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ IMMEDIATE ACTION REQUIRED</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">This incident requires immediate follow-up per OSHA guidelines.</p>
      </div>
      
      ${section('Incident Details', `
        <div style="white-space: pre-wrap; font-family: inherit;">${data.needleStickInjury || 'No details provided'}</div>
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `NEEDLE STICK INJURY REPORT - IMMEDIATE ACTION REQUIRED

INCIDENT DETAILS
${data.needleStickInjury || 'No details provided'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🚨 URGENT: Needle Stick Injury - ${data.practice || 'Unknown Practice'}` };
};

// ============================================
// EQUIPMENT MALFUNCTION TEMPLATES
// ============================================

export const generateEquipmentEmail = (data) => {
  const html = `
    ${emailHeader(`🔧 Equipment Malfunction: ${data.equipmentCategory || 'Unknown'}`, '#ef4444')}
    <div style="padding: 20px;">
      ${section('Equipment Information', `
        ${row('Category', data.equipmentCategory)}
        ${row('Equipment Name', data.equipmentName)}
        ${row('Manufacturer', data.manufacturer)}
        ${row('Model', data.model)}
        ${row('Serial Number', data.serialNumber)}
        ${row('Location/Operatory', data.location)}
      `)}
      
      ${section('Incident Details', `
        ${row('Date of Event', formatDate(data.dateOfEvent))}
        ${row('Time of Event', data.timeOfEvent)}
        ${row('Issue Type', data.issueType)}
        ${row('Patient Impact', data.patientImpact)}
        ${row('Procedure Impacted', data.procedureImpacted)}
        ${row('Downtime', data.downtimeMinutes ? `${data.downtimeMinutes} minutes` : null)}
      `, data.patientImpact === 'Yes' ? '#fef2f2' : '#f9fafb')}
      
      ${data.description ? section('Description of Issue', `
        <div style="white-space: pre-wrap;">${data.description}</div>
      `) : ''}
      
      ${data.actionTaken ? section('Action Taken', `
        <div style="white-space: pre-wrap;">${data.actionTaken}</div>
      `) : ''}
      
      ${section('Follow-up Status', `
        ${row('Vendor Contacted', data.vendorContacted)}
        ${data.vendorTicket ? row('Vendor Ticket #', data.vendorTicket) : ''}
        ${row('Requires Follow-up', data.requiresFollowUp)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `EQUIPMENT MALFUNCTION REPORT

EQUIPMENT INFORMATION
Category: ${data.equipmentCategory || 'N/A'}
Equipment Name: ${data.equipmentName || 'N/A'}
Manufacturer: ${data.manufacturer || 'N/A'}
Model: ${data.model || 'N/A'}
Serial Number: ${data.serialNumber || 'N/A'}
Location/Operatory: ${data.location || 'N/A'}

INCIDENT DETAILS
Date of Event: ${formatDate(data.dateOfEvent)}
Time of Event: ${data.timeOfEvent || 'N/A'}
Issue Type: ${data.issueType || 'N/A'}
Patient Impact: ${data.patientImpact || 'No'}
Procedure Impacted: ${data.procedureImpacted || 'N/A'}
Downtime: ${data.downtimeMinutes ? `${data.downtimeMinutes} minutes` : 'N/A'}

DESCRIPTION OF ISSUE
${data.description || 'N/A'}

ACTION TAKEN
${data.actionTaken || 'N/A'}

FOLLOW-UP STATUS
Vendor Contacted: ${data.vendorContacted || 'No'}
Vendor Ticket #: ${data.vendorTicket || 'N/A'}
Requires Follow-up: ${data.requiresFollowUp || 'No'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔧 Equipment Malfunction: ${data.equipmentName || data.equipmentCategory || 'Unknown'} - ${data.practice}` };
};

// ============================================
// PATIENT COMPLAINT TEMPLATES
// ============================================

export const generateComplaintEmail = (data) => {
  const html = `
    ${emailHeader(`📝 Patient Complaint: ${data.complaintCategory || 'General'}`, '#f59e0b')}
    <div style="padding: 20px;">
      ${section('Incident Details', `
        ${row('Category', data.complaintCategory)}
        ${row('Date of Event', formatDate(data.dateOfEvent))}
        ${row('Location/Operatory', data.location)}
        ${row('Severity', data.severity)}
      `)}
      
      ${section('Staff and Context', `
        ${row('Staff Involved', data.staffInvolved)}
        ${row('Area of Concern', data.patientExperienceArea)}
      `)}
      
      ${data.description ? section('Complaint Description', `
        <div style="white-space: pre-wrap;">${data.description}</div>
      `, '#fef2f2') : ''}
      
      ${data.desiredResolution ? section('Desired Resolution', `
        <div style="white-space: pre-wrap;">${data.desiredResolution}</div>
      `) : ''}
      
      ${section('Follow-up Information', `
        ${row('Contacted By', data.contactedBy)}
        ${row('Follow-up Needed', data.followUpNeeded)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `PATIENT COMPLAINT REPORT

INCIDENT DETAILS
Category: ${data.complaintCategory || 'N/A'}
Date of Event: ${formatDate(data.dateOfEvent)}
Location/Operatory: ${data.location || 'N/A'}
Severity: ${data.severity || 'N/A'}

STAFF AND CONTEXT
Staff Involved: ${data.staffInvolved || 'N/A'}
Area of Concern: ${data.patientExperienceArea || 'N/A'}

COMPLAINT DESCRIPTION
${data.description || 'N/A'}

DESIRED RESOLUTION
${data.desiredResolution || 'N/A'}

FOLLOW-UP INFORMATION
Contacted By: ${data.contactedBy || 'N/A'}
Follow-up Needed: ${data.followUpNeeded || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `📝 Patient Complaint (${data.complaintCategory || 'General'}) - ${data.practice}` };
};

// ============================================
// SCHEDULING/COMMUNICATION TEMPLATES
// ============================================

export const generateSchedulingEmail = (data) => {
  const html = `
    ${emailHeader(`📅 Scheduling/Communication Error: ${data.schedulingCategory || 'General'}`, '#f59e0b')}
    <div style="padding: 20px;">
      ${section('Incident Details', `
        ${row('Category', data.schedulingCategory)}
        ${row('Date of Issue', formatDate(data.dateOfIssue))}
        ${row('Team/Department', data.involvedTeam)}
      `)}
      
      ${section('Error Classification', `
        ${row('Error Type', data.errorType)}
        ${row('Impact', data.impact)}
        ${row('Recurrence Risk', data.recurrenceRisk)}
      `)}
      
      ${data.description ? section('Description', `
        <div style="white-space: pre-wrap;">${data.description}</div>
      `) : ''}
      
      ${data.correction ? section('Correction/Prevention', `
        <div style="white-space: pre-wrap;">${data.correction}</div>
      `) : ''}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `SCHEDULING/COMMUNICATION ERROR REPORT

INCIDENT DETAILS
Category: ${data.schedulingCategory || 'N/A'}
Date of Issue: ${formatDate(data.dateOfIssue)}
Team/Department: ${data.involvedTeam || 'N/A'}

ERROR CLASSIFICATION
Error Type: ${data.errorType || 'N/A'}
Impact: ${data.impact || 'N/A'}
Recurrence Risk: ${data.recurrenceRisk || 'N/A'}

DESCRIPTION
${data.description || 'N/A'}

CORRECTION/PREVENTION
${data.correction || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `📅 Scheduling Error: ${data.schedulingCategory || 'General'} - ${data.practice}` };
};

// ============================================
// AGGRESSIVE PATIENT TEMPLATE
// ============================================

export const generateAggressivePatientEmail = (data) => {
  const behaviorsList = formatList(data.behaviors);
  const deescalationList = formatList(data.deescalationMethods);
  
  const html = `
    ${emailHeader('⚠️ Aggressive Patient Interaction', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ SAFETY INCIDENT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">An aggressive patient interaction has been reported.</p>
      </div>
      
      ${section('Incident Details', `
        ${row('Date of Incident', formatDate(data.incidentDate))}
        ${row('Time of Incident', data.incidentTime)}
        ${row('Location/Operatory', data.location)}
      `)}
      
      ${section('Involved Parties', `
        ${row('Involved Party Type', data.involvedPartyType)}
        ${row('Staff Involved', data.staffInvolved)}
      `)}
      
      ${section('Behavioral Assessment', `
        ${row('Observed Behaviors', behaviorsList)}
        ${row('Weapons Present', data.weaponsPresent)}
        ${row('Injury Occurred', data.injuryOccurred)}
        ${data.injuryOccurred === 'Yes' && data.injuryDetails ? row('Injury Details', data.injuryDetails) : ''}
      `, data.injuryOccurred === 'Yes' ? '#fef2f2' : '#f9fafb')}
      
      ${section('Response Actions', `
        ${row('De-escalation Methods', deescalationList)}
        ${row('Law Enforcement Involved', data.lawEnforcementInvolved)}
        ${row('Outcome', data.outcome)}
      `)}
      
      ${section('Follow-up', `
        ${row('Follow-up Needed', data.followUpNeeded)}
        ${data.followUpNeeded === 'Yes' && data.followUpPlan ? row('Follow-up Plan', data.followUpPlan) : ''}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `AGGRESSIVE PATIENT INTERACTION REPORT

INCIDENT DETAILS
Date of Incident: ${formatDate(data.incidentDate)}
Time of Incident: ${data.incidentTime || 'N/A'}
Location/Operatory: ${data.location || 'N/A'}

INVOLVED PARTIES
Involved Party Type: ${data.involvedPartyType || 'N/A'}
Staff Involved: ${data.staffInvolved || 'N/A'}

BEHAVIORAL ASSESSMENT
Observed Behaviors: ${behaviorsList}
Weapons Present: ${data.weaponsPresent || 'No'}
Injury Occurred: ${data.injuryOccurred || 'No'}
${data.injuryOccurred === 'Yes' && data.injuryDetails ? `Injury Details: ${data.injuryDetails}` : ''}

RESPONSE ACTIONS
De-escalation Methods: ${deescalationList}
Law Enforcement Involved: ${data.lawEnforcementInvolved || 'No'}
Outcome: ${data.outcome || 'N/A'}

FOLLOW-UP
Follow-up Needed: ${data.followUpNeeded || 'No'}
${data.followUpNeeded === 'Yes' && data.followUpPlan ? `Follow-up Plan: ${data.followUpPlan}` : ''}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `⚠️ Aggressive Patient Interaction - ${data.practice}` };
};

// ============================================
// HIPAA FORM TEMPLATES
// ============================================

export const generateUnauthorizedDisclosureEmail = (data) => {
  const phiTypesList = formatList(data.phiTypes);
  
  const html = `
    ${emailHeader('🔒 HIPAA: Unauthorized Disclosure', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ HIPAA COMPLIANCE INCIDENT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">This incident may require reporting per HIPAA regulations.</p>
        ${data.ocrReportRequired ? `<p style="margin: 5px 0 0 0; color: #991b1b; font-weight: bold;">⚠️ OCR reporting required (500+ individuals affected)</p>` : ''}
      </div>
      
      ${section('Incident Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Date Occurred', formatDate(data.dateOccurred))}
        ${row('Time Occurred', data.timeOccurred)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Risk Level', data.riskLevel)}
      `)}
      
      ${section('Affected Patients', `
        ${row('Number of Patients Affected', data.patientsAffected)}
        ${row('Patient Names (if known)', data.patientNames)}
        ${row('Types of PHI Disclosed', phiTypesList)}
      `, '#fef2f2')}
      
      ${section('Disclosure Details', `
        ${row('Method of Disclosure', data.disclosureMethod)}
        ${row('Recipient Information', data.recipientInfo)}
      `)}
      
      ${data.incidentDescription ? section('Incident Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.causeOfIncident ? section('Cause of Disclosure', `
        <div style="white-space: pre-wrap;">${data.causeOfIncident}</div>
      `) : ''}
      
      ${data.immediateActions ? section('Immediate Actions Taken', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${section('Patient Notification', `
        ${row('Patient Notified', formatYesNo(data.patientNotified))}
        ${data.patientNotified ? row('Notification Date', formatDate(data.patientNotificationDate)) : ''}
        ${data.patientNotified ? row('Notification Method', data.patientNotificationMethod) : ''}
      `)}
      
      ${data.preventiveMeasures ? section('Preventive Measures', `
        <div style="white-space: pre-wrap;">${data.preventiveMeasures}</div>
      `) : ''}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `HIPAA INCIDENT: UNAUTHORIZED DISCLOSURE

INCIDENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Date Occurred: ${formatDate(data.dateOccurred)}
Time Occurred: ${data.timeOccurred || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}
Risk Level: ${data.riskLevel || 'N/A'}

AFFECTED PATIENTS
Number of Patients Affected: ${data.patientsAffected || 'N/A'}
Patient Names: ${data.patientNames || 'N/A'}
Types of PHI Disclosed: ${phiTypesList}

DISCLOSURE DETAILS
Method of Disclosure: ${data.disclosureMethod || 'N/A'}
Recipient Information: ${data.recipientInfo || 'N/A'}

INCIDENT DESCRIPTION
${data.incidentDescription || 'N/A'}

CAUSE OF DISCLOSURE
${data.causeOfIncident || 'N/A'}

IMMEDIATE ACTIONS TAKEN
${data.immediateActions || 'N/A'}

PATIENT NOTIFICATION
Patient Notified: ${formatYesNo(data.patientNotified)}
${data.patientNotified ? `Notification Date: ${formatDate(data.patientNotificationDate)}` : ''}
${data.patientNotified ? `Notification Method: ${data.patientNotificationMethod || 'N/A'}` : ''}

PREVENTIVE MEASURES
${data.preventiveMeasures || 'N/A'}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔒 HIPAA Incident: Unauthorized Disclosure - ${data.practice}` };
};

export const generateImproperAccessEmail = (data) => {
  const phiTypesList = formatList(data.phiTypes);
  
  const html = `
    ${emailHeader('🔒 HIPAA: Improper Access', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ HIPAA COMPLIANCE INCIDENT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Unauthorized staff access to PHI has been reported.</p>
      </div>
      
      ${section('Incident Details', `
        ${row('Date/Time Discovered', formatDate(data.dateTimeDiscovered))}
        ${row('Date/Time Occurred', formatDate(data.dateTimeOccurred))}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Staff Member Involved', data.staffMemberInvolved)}
      `)}
      
      ${section('Affected Information', `
        ${row('Number of Patients Affected', data.patientsAffected)}
        ${row('Types of PHI Accessed', phiTypesList)}
        ${row('Access Method', data.accessMethod)}
        ${row('Reason for Access', data.reasonForAccess)}
      `, '#fef2f2')}
      
      ${data.incidentDescription ? section('Incident Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.immediateActions ? section('Immediate Actions Taken', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${data.disciplinaryAction ? section('Disciplinary Action', `
        <div style="white-space: pre-wrap;">${data.disciplinaryAction}</div>
      `) : ''}
      
      ${data.preventiveMeasures ? section('Preventive Measures', `
        <div style="white-space: pre-wrap;">${data.preventiveMeasures}</div>
      `) : ''}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `HIPAA INCIDENT: IMPROPER ACCESS

INCIDENT DETAILS
Date/Time Discovered: ${formatDate(data.dateTimeDiscovered)}
Date/Time Occurred: ${formatDate(data.dateTimeOccurred)}
Discovered By: ${data.discoveredBy || 'N/A'}
Staff Member Involved: ${data.staffMemberInvolved || 'N/A'}

AFFECTED INFORMATION
Number of Patients Affected: ${data.patientsAffected || 'N/A'}
Types of PHI Accessed: ${phiTypesList}
Access Method: ${data.accessMethod || 'N/A'}
Reason for Access: ${data.reasonForAccess || 'N/A'}

INCIDENT DESCRIPTION
${data.incidentDescription || 'N/A'}

IMMEDIATE ACTIONS TAKEN
${data.immediateActions || 'N/A'}

DISCIPLINARY ACTION
${data.disciplinaryAction || 'N/A'}

PREVENTIVE MEASURES
${data.preventiveMeasures || 'N/A'}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔒 HIPAA Incident: Improper Access - ${data.practice}` };
};

export const generateLostRecordsEmail = (data) => {
  const phiTypesList = formatList(data.phiTypes);
  
  const html = `
    ${emailHeader('🔒 HIPAA: Lost/Misplaced Records', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ HIPAA COMPLIANCE INCIDENT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Lost or misplaced PHI records have been reported.</p>
      </div>
      
      ${section('Incident Details', `
        ${row('Date/Time Discovered', formatDate(data.dateTimeDiscovered))}
        ${row('Date/Time Lost', formatDate(data.dateTimeOccurred))}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Type of Records', data.recordType)}
        ${row('Record Format', data.recordFormat)}
      `)}
      
      ${section('Affected Information', `
        ${row('Number of Patients Affected', data.patientsAffected)}
        ${row('Types of PHI in Records', phiTypesList)}
        ${row('Last Known Location', data.lastKnownLocation)}
      `, '#fef2f2')}
      
      ${data.circumstances ? section('Circumstances of Loss', `
        <div style="white-space: pre-wrap;">${data.circumstances}</div>
      `) : ''}
      
      ${data.searchEfforts ? section('Search Efforts', `
        <div style="white-space: pre-wrap;">${data.searchEfforts}</div>
      `) : ''}
      
      ${data.incidentDescription ? section('Additional Details', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.immediateActions ? section('Immediate Actions Taken', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${section('Response', `
        ${row('Law Enforcement Contacted', formatYesNo(data.lawEnforcementContacted))}
      `)}
      
      ${data.preventiveMeasures ? section('Preventive Measures', `
        <div style="white-space: pre-wrap;">${data.preventiveMeasures}</div>
      `) : ''}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `HIPAA INCIDENT: LOST/MISPLACED RECORDS

INCIDENT DETAILS
Date/Time Discovered: ${formatDate(data.dateTimeDiscovered)}
Date/Time Lost: ${formatDate(data.dateTimeOccurred)}
Discovered By: ${data.discoveredBy || 'N/A'}
Type of Records: ${data.recordType || 'N/A'}
Record Format: ${data.recordFormat || 'N/A'}

AFFECTED INFORMATION
Number of Patients Affected: ${data.patientsAffected || 'N/A'}
Types of PHI in Records: ${phiTypesList}
Last Known Location: ${data.lastKnownLocation || 'N/A'}

CIRCUMSTANCES OF LOSS
${data.circumstances || 'N/A'}

SEARCH EFFORTS
${data.searchEfforts || 'N/A'}

ADDITIONAL DETAILS
${data.incidentDescription || 'N/A'}

IMMEDIATE ACTIONS TAKEN
${data.immediateActions || 'N/A'}

RESPONSE
Law Enforcement Contacted: ${formatYesNo(data.lawEnforcementContacted)}

PREVENTIVE MEASURES
${data.preventiveMeasures || 'N/A'}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔒 HIPAA Incident: Lost/Misplaced Records - ${data.practice}` };
};

export const generateVendorBreachEmail = (data) => {
  const phiTypesList = formatList(data.phiTypes);
  
  const html = `
    ${emailHeader('🔒 HIPAA: Vendor/BAA Breach', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ HIPAA COMPLIANCE INCIDENT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">A vendor/business associate breach has been reported.</p>
      </div>
      
      ${section('Incident Details', `
        ${row('Date/Time Discovered', formatDate(data.dateTimeDiscovered))}
        ${row('Date/Time Occurred', formatDate(data.dateTimeOccurred))}
        ${row('Discovered By', data.discoveredBy)}
      `)}
      
      ${section('Vendor Information', `
        ${row('Vendor/BA Name', data.vendorName)}
        ${row('Vendor Contact', data.vendorContact)}
        ${row('BAA In Place', formatYesNo(data.baaInPlace))}
        ${row('Type of Breach', data.breachType)}
      `)}
      
      ${section('Affected Information', `
        ${row('Number of Patients Affected', data.patientsAffected)}
        ${row('Types of PHI Compromised', phiTypesList)}
      `, '#fef2f2')}
      
      ${data.breachDescription ? section('Breach Description', `
        <div style="white-space: pre-wrap;">${data.breachDescription}</div>
      `) : ''}
      
      ${section('Vendor Response', `
        ${row('Vendor Notification Date', formatDate(data.vendorNotificationDate))}
        ${data.vendorResponse ? `<div style="white-space: pre-wrap;">${data.vendorResponse}</div>` : '<p>No response recorded</p>'}
      `)}
      
      ${data.immediateActions ? section('Immediate Actions Taken', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${section('Notification Requirements', `
        ${row('OCR Notification Required', formatYesNo(data.ocrNotificationRequired))}
        ${row('Patient Notification Required', formatYesNo(data.patientNotificationRequired))}
      `)}
      
      ${data.preventiveMeasures ? section('Preventive Measures', `
        <div style="white-space: pre-wrap;">${data.preventiveMeasures}</div>
      `) : ''}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `HIPAA INCIDENT: VENDOR/BAA BREACH

INCIDENT DETAILS
Date/Time Discovered: ${formatDate(data.dateTimeDiscovered)}
Date/Time Occurred: ${formatDate(data.dateTimeOccurred)}
Discovered By: ${data.discoveredBy || 'N/A'}

VENDOR INFORMATION
Vendor/BA Name: ${data.vendorName || 'N/A'}
Vendor Contact: ${data.vendorContact || 'N/A'}
BAA In Place: ${formatYesNo(data.baaInPlace)}
Type of Breach: ${data.breachType || 'N/A'}

AFFECTED INFORMATION
Number of Patients Affected: ${data.patientsAffected || 'N/A'}
Types of PHI Compromised: ${phiTypesList}

BREACH DESCRIPTION
${data.breachDescription || 'N/A'}

VENDOR RESPONSE
Vendor Notification Date: ${formatDate(data.vendorNotificationDate)}
${data.vendorResponse || 'No response recorded'}

IMMEDIATE ACTIONS TAKEN
${data.immediateActions || 'N/A'}

NOTIFICATION REQUIREMENTS
OCR Notification Required: ${formatYesNo(data.ocrNotificationRequired)}
Patient Notification Required: ${formatYesNo(data.patientNotificationRequired)}

PREVENTIVE MEASURES
${data.preventiveMeasures || 'N/A'}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔒 HIPAA Incident: Vendor/BAA Breach - ${data.practice}` };
};

export const generatePatientRightsEmail = (data) => {
  const violationTypeLabels = {
    'access_denied': 'Access to Records Denied',
    'access_delayed': 'Access to Records Delayed',
    'amendment_denied': 'Amendment Request Denied Improperly',
    'amendment_delayed': 'Amendment Request Delayed',
    'restriction_denied': 'Restriction Request Denied Improperly',
    'restriction_ignored': 'Restriction Request Ignored',
    'accounting_denied': 'Accounting of Disclosures Denied',
    'accounting_incomplete': 'Accounting of Disclosures Incomplete',
    'other': 'Other'
  };
  
  const requestTypeLabels = {
    'access_records': 'Access to Medical Records',
    'amend_records': 'Amendment to Medical Records',
    'restrict_disclosure': 'Restriction on Disclosure',
    'accounting_disclosures': 'Accounting of Disclosures',
    'alternative_communication': 'Alternative Communication Method',
    'other': 'Other'
  };
  
  const violationLabel = violationTypeLabels[data.violationType] || data.violationType || 'N/A';
  const requestLabel = requestTypeLabels[data.requestType] || data.requestType || 'N/A';
  
  const html = `
    ${emailHeader('🔒 HIPAA: Patient Rights Violation', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ HIPAA PATIENT RIGHTS VIOLATION</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">A patient rights violation has been reported.</p>
      </div>
      
      ${section('Incident Details', `
        ${row('Date/Time Discovered', formatDate(data.dateTimeDiscovered))}
        ${row('Date/Time Occurred', formatDate(data.dateTimeOccurred))}
        ${row('Discovered By', data.discoveredBy)}
      `)}
      
      ${section('Patient Information', `
        ${row('Patient Name', data.patientName)}
        ${row('Patient ID', data.patientId)}
      `)}
      
      ${section('Violation Details', `
        ${row('Type of Violation', violationLabel)}
        ${row('Patient Request Type', requestLabel)}
        ${row('Request Date', formatDate(data.requestDate))}
        ${data.requestDescription ? `<p style="margin: 10px 0 5px 0;"><strong>Request Description:</strong></p><div style="white-space: pre-wrap; padding-left: 10px;">${data.requestDescription}</div>` : ''}
      `, '#fef2f2')}
      
      ${section('Our Response', `
        ${row('Response Date', formatDate(data.responseDate))}
        ${row('Response Provided', formatYesNo(data.responseProvided))}
        ${data.reasonForDenial ? `<p style="margin: 10px 0 5px 0;"><strong>Reason for Denial/Delay:</strong></p><div style="white-space: pre-wrap; padding-left: 10px;">${data.reasonForDenial}</div>` : ''}
      `)}
      
      ${data.incidentDescription ? section('Incident Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${section('Response Actions', `
        ${row('Patient Notified of Violation', formatYesNo(data.patientNotified))}
        ${data.immediateActions ? `<p style="margin: 10px 0 5px 0;"><strong>Immediate Actions:</strong></p><div style="white-space: pre-wrap; padding-left: 10px;">${data.immediateActions}</div>` : ''}
        ${data.correctiveActions ? `<p style="margin: 10px 0 5px 0;"><strong>Corrective Actions:</strong></p><div style="white-space: pre-wrap; padding-left: 10px;">${data.correctiveActions}</div>` : ''}
      `)}
      
      ${data.preventiveMeasures ? section('Preventive Measures', `
        <div style="white-space: pre-wrap;">${data.preventiveMeasures}</div>
      `) : ''}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `HIPAA INCIDENT: PATIENT RIGHTS VIOLATION

INCIDENT DETAILS
Date/Time Discovered: ${formatDate(data.dateTimeDiscovered)}
Date/Time Occurred: ${formatDate(data.dateTimeOccurred)}
Discovered By: ${data.discoveredBy || 'N/A'}

PATIENT INFORMATION
Patient Name: ${data.patientName || 'N/A'}
Patient ID: ${data.patientId || 'N/A'}

VIOLATION DETAILS
Type of Violation: ${violationLabel}
Patient Request Type: ${requestLabel}
Request Date: ${formatDate(data.requestDate)}
Request Description: ${data.requestDescription || 'N/A'}

OUR RESPONSE
Response Date: ${formatDate(data.responseDate)}
Response Provided: ${formatYesNo(data.responseProvided)}
Reason for Denial/Delay: ${data.reasonForDenial || 'N/A'}

INCIDENT DESCRIPTION
${data.incidentDescription || 'N/A'}

RESPONSE ACTIONS
Patient Notified of Violation: ${formatYesNo(data.patientNotified)}
Immediate Actions: ${data.immediateActions || 'N/A'}
Corrective Actions: ${data.correctiveActions || 'N/A'}

PREVENTIVE MEASURES
${data.preventiveMeasures || 'N/A'}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔒 HIPAA: Patient Rights Violation - ${data.practice}` };
};

export const generateTransmissionErrorEmail = (data) => {
  const phiTypesList = formatList(data.phiTypes);
  
  const riskColors = {
    'low': '#22c55e',
    'medium': '#f59e0b',
    'high': '#ef4444'
  };
  const riskColor = riskColors[data.riskLevel] || '#f59e0b';
  
  const html = `
    ${emailHeader('🔒 HIPAA: Transmission Error', '#f59e0b')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ HIPAA TRANSMISSION ERROR</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">PHI was sent to an unintended recipient.</p>
        <p style="margin: 5px 0 0 0;"><span style="background-color: ${riskColor}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px;">Risk Level: ${(data.riskLevel || 'Unknown').toUpperCase()}</span></p>
      </div>
      
      ${section('Incident Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Patients Affected', data.patientsAffected)}
        ${row('Risk Level', (data.riskLevel || 'N/A').charAt(0).toUpperCase() + (data.riskLevel || 'N/A').slice(1))}
      `)}
      
      ${section('Transmission Details', `
        ${row('Transmission Method', data.transmissionMethod)}
        ${row('Cause of Error', data.causeOfError)}
        ${row('Intended Recipient', data.intendedRecipient)}
        ${row('Actual Recipient', data.actualRecipient)}
        ${row('Types of PHI Transmitted', phiTypesList)}
      `, '#fef2f2')}
      
      ${data.incidentDescription ? section('Incident Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.immediateActions ? section('Immediate Actions Taken', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${section('Recovery Actions', `
        ${row('Recipient Contacted', formatYesNo(data.recipientContacted))}
        ${data.recipientContacted && data.recipientResponse ? `<p style="margin: 10px 0 5px 0;"><strong>Recipient Response:</strong></p><div style="white-space: pre-wrap; padding-left: 10px;">${data.recipientResponse}</div>` : ''}
        ${row('PHI Recovered/Destroyed', formatYesNo(data.phiRecovered))}
        ${data.phiRecovered && data.recoveryMethod ? row('Recovery Method', data.recoveryMethod) : ''}
      `)}
      
      ${section('Patient Notification', `
        ${row('Patient(s) Notified', formatYesNo(data.patientNotified))}
        ${data.patientNotified ? row('Notification Date', formatDate(data.patientNotificationDate)) : ''}
      `)}
      
      ${data.preventiveMeasures ? section('Preventive Measures', `
        <div style="white-space: pre-wrap;">${data.preventiveMeasures}</div>
      `) : ''}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `HIPAA INCIDENT: TRANSMISSION ERROR

Risk Level: ${(data.riskLevel || 'Unknown').toUpperCase()}

INCIDENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}
Patients Affected: ${data.patientsAffected || 'N/A'}

TRANSMISSION DETAILS
Transmission Method: ${data.transmissionMethod || 'N/A'}
Cause of Error: ${data.causeOfError || 'N/A'}
Intended Recipient: ${data.intendedRecipient || 'N/A'}
Actual Recipient: ${data.actualRecipient || 'N/A'}
Types of PHI Transmitted: ${phiTypesList}

INCIDENT DESCRIPTION
${data.incidentDescription || 'N/A'}

IMMEDIATE ACTIONS TAKEN
${data.immediateActions || 'N/A'}

RECOVERY ACTIONS
Recipient Contacted: ${formatYesNo(data.recipientContacted)}
${data.recipientContacted && data.recipientResponse ? `Recipient Response: ${data.recipientResponse}` : ''}
PHI Recovered/Destroyed: ${formatYesNo(data.phiRecovered)}
${data.phiRecovered && data.recoveryMethod ? `Recovery Method: ${data.recoveryMethod}` : ''}

PATIENT NOTIFICATION
Patient(s) Notified: ${formatYesNo(data.patientNotified)}
${data.patientNotified ? `Notification Date: ${formatDate(data.patientNotificationDate)}` : ''}

PREVENTIVE MEASURES
${data.preventiveMeasures || 'N/A'}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔒 HIPAA: Transmission Error (${(data.riskLevel || 'Unknown').toUpperCase()} Risk) - ${data.practice}` };
};

// Generic HIPAA email for forms using the generic HipaaForm
export const generateHIPAAEmail = (data, subType) => {
  const subTypeLabels = {
    'UnauthorizedDisclosure': 'Unauthorized Disclosure',
    'ImproperAccess': 'Improper Access',
    'LostRecords': 'Lost/Misplaced Records',
    'VendorBreach': 'Vendor/BAA Breach',
    'PatientRights': 'Patient Rights Violation',
    'TransmissionError': 'Transmission Error',
    'patientRights': 'Patient Rights Violation'
  };
  
  // Route to specific handlers if subType is provided
  if (subType === 'UnauthorizedDisclosure') return generateUnauthorizedDisclosureEmail(data);
  if (subType === 'ImproperAccess') return generateImproperAccessEmail(data);
  if (subType === 'LostRecords') return generateLostRecordsEmail(data);
  if (subType === 'VendorBreach') return generateVendorBreachEmail(data);
  if (subType === 'PatientRights' || subType === 'patientRights') return generatePatientRightsEmail(data);
  if (subType === 'TransmissionError') return generateTransmissionErrorEmail(data);
  
  const label = subTypeLabels[subType] || data.incidentType || 'HIPAA Incident';
  const phiTypesList = formatList(data.phiTypes);
  
  const html = `
    ${emailHeader(`🔒 HIPAA Incident: ${label}`, '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">⚠️ HIPAA COMPLIANCE INCIDENT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">This incident may require reporting per HIPAA regulations.</p>
      </div>
      
      ${section('Incident Classification', `
        ${row('Type', label)}
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Date Occurred', formatDate(data.dateOccurred))}
        ${row('Time Occurred', data.timeOccurred)}
        ${row('Discovered By', data.discoveredBy)}
      `)}
      
      ${section('Affected Information', `
        ${row('Patients Affected', data.patientsAffected)}
        ${row('Types of PHI', phiTypesList)}
      `, '#fef2f2')}
      
      ${data.incidentDescription ? section('Incident Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.immediateActions ? section('Immediate Actions', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${section('Risk Assessment', `
        ${row('Risk Level', data.riskAssessment || data.riskLevel)}
        ${row('Patient Notification Required', formatYesNo(data.patientNotified))}
        ${data.patientNotified ? row('Notification Date', formatDate(data.patientNotificationDate)) : ''}
      `)}
      
      ${data.preventiveMeasures ? section('Preventive Measures', `
        <div style="white-space: pre-wrap;">${data.preventiveMeasures}</div>
      `) : ''}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `HIPAA COMPLIANCE INCIDENT - ${label.toUpperCase()}

INCIDENT CLASSIFICATION
Type: ${label}
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Date Occurred: ${formatDate(data.dateOccurred)}
Time Occurred: ${data.timeOccurred || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}

AFFECTED INFORMATION
Patients Affected: ${data.patientsAffected || 'N/A'}
Types of PHI: ${phiTypesList}

INCIDENT DESCRIPTION
${data.incidentDescription || 'N/A'}

IMMEDIATE ACTIONS
${data.immediateActions || 'N/A'}

RISK ASSESSMENT
Risk Level: ${data.riskAssessment || data.riskLevel || 'N/A'}
Patient Notification Required: ${formatYesNo(data.patientNotified)}
${data.patientNotified ? `Notification Date: ${formatDate(data.patientNotificationDate)}` : ''}

PREVENTIVE MEASURES
${data.preventiveMeasures || 'N/A'}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🔒 HIPAA Incident: ${label} - ${data.practice}` };
};

// ============================================
// IT SECURITY EVENT TEMPLATES
// ============================================

export const generateDataBreachEmail = (data) => {
  const affectedSystemsList = formatList(data.affectedSystems);
  const dataTypesList = formatList(data.dataTypes);
  const notificationsList = formatList(data.notificationsRequired);
  
  const html = `
    ${emailHeader('🛡️ IT Security: Data Breach', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">🚨 SECURITY INCIDENT ALERT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Priority: ${(data.priority || 'medium').toUpperCase()}</p>
      </div>
      
      ${section('Event Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Priority', data.priority)}
      `)}
      
      ${section('Affected Systems & Data', `
        ${row('Affected Systems', affectedSystemsList)}
        ${row('Estimated Patients Affected', data.estimatedPatientCount)}
        ${row('Data Types Affected', dataTypesList)}
        ${row('Breach Method', data.breachMethod)}
      `, '#fef2f2')}
      
      ${data.incidentDescription ? section('Event Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.immediateActions ? section('Immediate Actions Taken', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${section('Containment & Notifications', `
        ${row('Containment Status', data.containmentStatus)}
        ${row('Notifications Required', notificationsList)}
      `)}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `IT SECURITY: DATA BREACH

Priority: ${(data.priority || 'medium').toUpperCase()}

EVENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}

AFFECTED SYSTEMS & DATA
Affected Systems: ${affectedSystemsList}
Estimated Patients Affected: ${data.estimatedPatientCount || 'Unknown'}
Data Types Affected: ${dataTypesList}
Breach Method: ${data.breachMethod || 'N/A'}

EVENT DESCRIPTION
${data.incidentDescription || 'N/A'}

IMMEDIATE ACTIONS TAKEN
${data.immediateActions || 'N/A'}

CONTAINMENT & NOTIFICATIONS
Containment Status: ${data.containmentStatus || 'N/A'}
Notifications Required: ${notificationsList}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🛡️ IT Security: Data Breach (${(data.priority || 'medium').toUpperCase()}) - ${data.practice}` };
};

export const generateRansomwareEmail = (data) => {
  const affectedSystemsList = formatList(data.affectedSystems);
  const systemsOfflineList = formatList(data.systemsOffline);
  const immediateActionsList = formatList(data.immediateActions);
  const notificationsList = formatList(data.notificationsRequired);
  
  const html = `
    ${emailHeader('🛡️ IT Security: RANSOMWARE ATTACK', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold; font-size: 18px;">🚨 CRITICAL: RANSOMWARE DETECTED</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Follow incident response protocol immediately.</p>
      </div>
      
      ${section('Event Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Ransomware Type', data.ransomwareType)}
      `)}
      
      ${section('System Status', `
        ${row('Affected Systems', affectedSystemsList)}
        ${row('Systems Currently Offline', systemsOfflineList)}
        ${row('Encryption Status', data.encryptionStatus)}
        ${row('Backup Status', data.backupStatus)}
      `, '#fef2f2')}
      
      ${data.ransom && data.ransom.demanded ? section('Ransom Demand', `
        ${row('Ransom Demanded', 'Yes')}
        ${row('Amount', data.ransom.amount ? `${data.ransom.amount} ${data.ransom.currency}` : 'Unknown')}
        ${row('Payment Method', data.ransom.paymentMethod)}
        ${row('Deadline', formatDate(data.ransom.deadline))}
      `, '#fef2f2') : ''}
      
      ${data.incidentDescription ? section('Event Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${section('Response Actions', `
        ${row('Immediate Actions Taken', immediateActionsList)}
        ${row('Law Enforcement Contacted', formatYesNo(data.lawEnforcementContacted))}
      `)}
      
      ${data.patientImpact ? section('Patient Care Impact', `
        <div style="white-space: pre-wrap;">${data.patientImpact}</div>
      `) : ''}
      
      ${section('Notifications Required', `
        <p style="margin: 5px 0;">${notificationsList}</p>
      `)}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `🚨 CRITICAL: RANSOMWARE ATTACK

EVENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}
Ransomware Type: ${data.ransomwareType || 'Unknown'}

SYSTEM STATUS
Affected Systems: ${affectedSystemsList}
Systems Currently Offline: ${systemsOfflineList}
Encryption Status: ${data.encryptionStatus || 'N/A'}
Backup Status: ${data.backupStatus || 'N/A'}

${data.ransom && data.ransom.demanded ? `RANSOM DEMAND
Ransom Demanded: Yes
Amount: ${data.ransom.amount ? `${data.ransom.amount} ${data.ransom.currency}` : 'Unknown'}
Payment Method: ${data.ransom.paymentMethod || 'N/A'}
Deadline: ${formatDate(data.ransom.deadline)}
` : ''}

EVENT DESCRIPTION
${data.incidentDescription || 'N/A'}

RESPONSE ACTIONS
Immediate Actions Taken: ${immediateActionsList}
Law Enforcement Contacted: ${formatYesNo(data.lawEnforcementContacted)}

PATIENT CARE IMPACT
${data.patientImpact || 'N/A'}

NOTIFICATIONS REQUIRED
${notificationsList}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🚨 CRITICAL: Ransomware Attack - ${data.practice}` };
};

export const generateCyberAttackEmail = (data) => {
  const affectedSystemsList = formatList(data.affectedSystems);
  const dataTypesList = formatList(data.dataTypes);
  const immediateActionsList = formatList(data.immediateActions);
  const notificationsList = formatList(data.notificationsRequired);
  
  const html = `
    ${emailHeader('🛡️ IT Security: Cyber Attack', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold; font-size: 18px;">🚨 CYBER ATTACK DETECTED</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Priority: ${(data.priority || 'high').toUpperCase()}</p>
      </div>
      
      ${section('Event Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Attack Type', data.attackType)}
        ${row('Attack Vector', data.attackVector)}
        ${row('Attack Source', data.attackSource)}
      `)}
      
      ${section('Affected Systems', `
        ${row('Systems Affected', affectedSystemsList)}
        ${row('Systems Currently Offline', formatYesNo(data.systemsOffline))}
        ${data.systemsOffline && data.offlineSystemsList ? `<p style="margin: 5px 0;"><strong>Offline Systems Details:</strong> ${data.offlineSystemsList}</p>` : ''}
        ${row('Estimated Patients Affected', data.estimatedPatientCount)}
      `, '#fef2f2')}
      
      ${section('Data Compromise Assessment', `
        ${row('Data May Be Compromised', formatYesNo(data.dataCompromised))}
        ${data.dataCompromised ? row('Types of Data Affected', dataTypesList) : ''}
      `, data.dataCompromised ? '#fef2f2' : '#f9fafb')}
      
      ${data.incidentDescription ? section('Event Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.patientImpact ? section('Patient Care Impact', `
        <div style="white-space: pre-wrap;">${data.patientImpact}</div>
      `) : ''}
      
      ${section('Response Actions', `
        ${row('Immediate Actions Taken', immediateActionsList)}
        ${row('Evidence Preserved', formatYesNo(data.evidencePreserved))}
        ${data.evidencePreserved && data.evidenceDescription ? `<p style="margin: 5px 0;"><strong>Evidence Details:</strong> ${data.evidenceDescription}</p>` : ''}
        ${row('Forensics Investigation Required', formatYesNo(data.forensicsRequired))}
        ${row('Law Enforcement Contacted', formatYesNo(data.lawEnforcementContacted))}
      `)}
      
      ${section('Notifications Required', `
        <p style="margin: 5px 0;">${notificationsList}</p>
      `)}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `🚨 CYBER ATTACK DETECTED

Priority: ${(data.priority || 'high').toUpperCase()}

EVENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}
Attack Type: ${data.attackType || 'N/A'}
Attack Vector: ${data.attackVector || 'N/A'}
Attack Source: ${data.attackSource || 'N/A'}

AFFECTED SYSTEMS
Systems Affected: ${affectedSystemsList}
Systems Currently Offline: ${formatYesNo(data.systemsOffline)}
${data.systemsOffline && data.offlineSystemsList ? `Offline Systems Details: ${data.offlineSystemsList}` : ''}
Estimated Patients Affected: ${data.estimatedPatientCount || 'Unknown'}

DATA COMPROMISE ASSESSMENT
Data May Be Compromised: ${formatYesNo(data.dataCompromised)}
${data.dataCompromised ? `Types of Data Affected: ${dataTypesList}` : ''}

EVENT DESCRIPTION
${data.incidentDescription || 'N/A'}

PATIENT CARE IMPACT
${data.patientImpact || 'N/A'}

RESPONSE ACTIONS
Immediate Actions Taken: ${immediateActionsList}
Evidence Preserved: ${formatYesNo(data.evidencePreserved)}
${data.evidencePreserved && data.evidenceDescription ? `Evidence Details: ${data.evidenceDescription}` : ''}
Forensics Investigation Required: ${formatYesNo(data.forensicsRequired)}
Law Enforcement Contacted: ${formatYesNo(data.lawEnforcementContacted)}

NOTIFICATIONS REQUIRED
${notificationsList}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🚨 IT Security: Cyber Attack (${(data.priority || 'high').toUpperCase()}) - ${data.practice}` };
};

export const generateSystemBreachEmail = (data) => {
  const breachedSystemsList = formatList(data.breachedSystems);
  const dataTypesList = formatList(data.dataTypes);
  const immediateActionsList = formatList(data.immediateActions);
  const accountsDisabledList = formatList(data.accountsDisabled);
  const notificationsList = formatList(data.notificationsRequired);
  
  const html = `
    ${emailHeader('🛡️ IT Security: System Breach', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold; font-size: 18px;">🚨 SYSTEM BREACH DETECTED</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Priority: ${(data.priority || 'high').toUpperCase()}</p>
      </div>
      
      ${section('Event Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Unauthorized User/Entity', data.unauthorizedUser)}
        ${row('Estimated Access Duration', data.accessDuration)}
      `)}
      
      ${section('Affected Systems', `
        ${row('Systems Breached', breachedSystemsList)}
        ${row('Access Method', data.accessMethod)}
        ${row('Access Level Obtained', data.accessLevel)}
        ${row('Estimated Patients Affected', data.estimatedPatientCount)}
      `, '#fef2f2')}
      
      ${section('Data Access', `
        ${row('Patient Data Accessed', formatYesNo(data.dataAccessed))}
        ${data.dataAccessed ? row('Types of Data Accessed', dataTypesList) : ''}
      `, data.dataAccessed ? '#fef2f2' : '#f9fafb')}
      
      ${data.incidentDescription ? section('Event Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${section('Security Response', `
        ${row('Immediate Actions', immediateActionsList)}
        ${row('Systems Secured', formatYesNo(data.systemsSecured))}
        ${row('Accounts Disabled', accountsDisabledList)}
        ${row('Passwords Changed', formatYesNo(data.passwordsChanged))}
        ${row('Audit Logs Reviewed', formatYesNo(data.auditLogsReviewed))}
      `)}
      
      ${section('Notifications Required', `
        <p style="margin: 5px 0;">${notificationsList}</p>
      `)}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `🚨 SYSTEM BREACH DETECTED

Priority: ${(data.priority || 'high').toUpperCase()}

EVENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}
Unauthorized User/Entity: ${data.unauthorizedUser || 'N/A'}
Estimated Access Duration: ${data.accessDuration || 'N/A'}

AFFECTED SYSTEMS
Systems Breached: ${breachedSystemsList}
Access Method: ${data.accessMethod || 'N/A'}
Access Level Obtained: ${data.accessLevel || 'N/A'}
Estimated Patients Affected: ${data.estimatedPatientCount || 'Unknown'}

DATA ACCESS
Patient Data Accessed: ${formatYesNo(data.dataAccessed)}
${data.dataAccessed ? `Types of Data Accessed: ${dataTypesList}` : ''}

EVENT DESCRIPTION
${data.incidentDescription || 'N/A'}

SECURITY RESPONSE
Immediate Actions: ${immediateActionsList}
Systems Secured: ${formatYesNo(data.systemsSecured)}
Accounts Disabled: ${accountsDisabledList}
Passwords Changed: ${formatYesNo(data.passwordsChanged)}
Audit Logs Reviewed: ${formatYesNo(data.auditLogsReviewed)}

NOTIFICATIONS REQUIRED
${notificationsList}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🚨 IT Security: System Breach (${(data.priority || 'high').toUpperCase()}) - ${data.practice}` };
};

export const generateEmailCompromiseEmail = (data) => {
  const compromisedAccountsList = formatList(data.compromisedAccounts);
  const dataTypesList = formatList(data.dataTypes);
  const immediateActionsList = formatList(data.immediateActions);
  const notificationsList = formatList(data.notificationsRequired);
  
  const html = `
    ${emailHeader('🛡️ IT Security: Email Compromise', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold; font-size: 18px;">🚨 EMAIL COMPROMISE DETECTED</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Priority: ${(data.priority || 'high').toUpperCase()}</p>
      </div>
      
      ${section('Event Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Type of Email Breach', data.breachType)}
      `)}
      
      ${section('Affected Accounts', `
        ${row('Types of Accounts Compromised', compromisedAccountsList)}
        ${row('Estimated Patients Affected', data.estimatedPatientCount)}
      `, '#fef2f2')}
      
      ${section('Data Compromise Assessment', `
        ${row('Sensitive Data May Be Compromised', formatYesNo(data.dataCompromised))}
        ${data.dataCompromised ? row('Types of Data Affected', dataTypesList) : ''}
      `, data.dataCompromised ? '#fef2f2' : '#f9fafb')}
      
      ${data.incidentDescription ? section('Event Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${section('Security Response', `
        ${row('Immediate Actions', immediateActionsList)}
        ${row('Accounts Secured', formatYesNo(data.accountsSecured))}
        ${row('Passwords Changed', formatYesNo(data.passwordsChanged))}
        ${row('MFA Enabled', formatYesNo(data.mfaEnabled))}
        ${row('Email History Reviewed', formatYesNo(data.emailsReviewed))}
      `)}
      
      ${section('Notifications Required', `
        <p style="margin: 5px 0;">${notificationsList}</p>
      `)}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `🚨 EMAIL COMPROMISE DETECTED

Priority: ${(data.priority || 'high').toUpperCase()}

EVENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}
Type of Email Breach: ${data.breachType || 'N/A'}

AFFECTED ACCOUNTS
Types of Accounts Compromised: ${compromisedAccountsList}
Estimated Patients Affected: ${data.estimatedPatientCount || 'Unknown'}

DATA COMPROMISE ASSESSMENT
Sensitive Data May Be Compromised: ${formatYesNo(data.dataCompromised)}
${data.dataCompromised ? `Types of Data Affected: ${dataTypesList}` : ''}

EVENT DESCRIPTION
${data.incidentDescription || 'N/A'}

SECURITY RESPONSE
Immediate Actions: ${immediateActionsList}
Accounts Secured: ${formatYesNo(data.accountsSecured)}
Passwords Changed: ${formatYesNo(data.passwordsChanged)}
MFA Enabled: ${formatYesNo(data.mfaEnabled)}
Email History Reviewed: ${formatYesNo(data.emailsReviewed)}

NOTIFICATIONS REQUIRED
${notificationsList}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🚨 IT Security: Email Compromise (${(data.priority || 'high').toUpperCase()}) - ${data.practice}` };
};

export const generateDeviceBreachEmail = (data) => {
  const deviceTypesList = formatList(data.deviceTypes);
  const dataTypesList = formatList(data.dataTypes);
  const immediateActionsList = formatList(data.immediateActions);
  const notificationsList = formatList(data.notificationsRequired);
  
  const html = `
    ${emailHeader('🛡️ IT Security: Device Theft/Loss', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold; font-size: 18px;">🚨 DEVICE THEFT/LOSS REPORTED</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Priority: ${(data.priority || 'high').toUpperCase()}</p>
      </div>
      
      ${section('Event Details', `
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Date Occurred', formatDate(data.dateOccurred))}
        ${row('Time Occurred', data.timeOccurred)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Type of Incident', data.incidentType)}
      `)}
      
      ${section('Device Information', `
        ${row('Types of Devices', deviceTypesList)}
        ${data.deviceDetails ? `<p style="margin: 10px 0 5px 0;"><strong>Device Details:</strong></p><div style="white-space: pre-wrap; padding-left: 10px;">${data.deviceDetails}</div>` : ''}
        ${row('Data on Device Encrypted', formatYesNo(data.dataEncrypted))}
      `)}
      
      ${section('Data on Device', `
        ${row('Estimated Patients Affected', data.estimatedPatientCount)}
        ${row('Types of Data on Device', dataTypesList)}
      `, '#fef2f2')}
      
      ${section('Location & Circumstances', `
        ${row('Location of Incident', data.location)}
        ${data.circumstances ? `<p style="margin: 10px 0 5px 0;"><strong>Circumstances:</strong></p><div style="white-space: pre-wrap; padding-left: 10px;">${data.circumstances}</div>` : ''}
      `)}
      
      ${data.incidentDescription ? section('Event Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${section('Response Actions', `
        ${row('Immediate Actions', immediateActionsList)}
        ${row('Device Recovered', formatYesNo(data.deviceRecovered))}
        ${data.deviceRecovered && data.recoveryDetails ? `<p style="margin: 5px 0;"><strong>Recovery Details:</strong> ${data.recoveryDetails}</p>` : ''}
        ${row('Law Enforcement Contacted', formatYesNo(data.lawEnforcementContacted))}
        ${row('Insurance Claim Filed', formatYesNo(data.insuranceClaim))}
      `)}
      
      ${section('Notifications Required', `
        <p style="margin: 5px 0;">${notificationsList}</p>
      `)}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `🚨 DEVICE THEFT/LOSS REPORTED

Priority: ${(data.priority || 'high').toUpperCase()}

EVENT DETAILS
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Date Occurred: ${formatDate(data.dateOccurred)}
Time Occurred: ${data.timeOccurred || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}
Type of Incident: ${data.incidentType || 'N/A'}

DEVICE INFORMATION
Types of Devices: ${deviceTypesList}
Device Details: ${data.deviceDetails || 'N/A'}
Data on Device Encrypted: ${formatYesNo(data.dataEncrypted)}

DATA ON DEVICE
Estimated Patients Affected: ${data.estimatedPatientCount || 'Unknown'}
Types of Data on Device: ${dataTypesList}

LOCATION & CIRCUMSTANCES
Location of Incident: ${data.location || 'N/A'}
Circumstances: ${data.circumstances || 'N/A'}

EVENT DESCRIPTION
${data.incidentDescription || 'N/A'}

RESPONSE ACTIONS
Immediate Actions: ${immediateActionsList}
Device Recovered: ${formatYesNo(data.deviceRecovered)}
${data.deviceRecovered && data.recoveryDetails ? `Recovery Details: ${data.recoveryDetails}` : ''}
Law Enforcement Contacted: ${formatYesNo(data.lawEnforcementContacted)}
Insurance Claim Filed: ${formatYesNo(data.insuranceClaim)}

NOTIFICATIONS REQUIRED
${notificationsList}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🚨 IT Security: Device Theft/Loss (${(data.priority || 'high').toUpperCase()}) - ${data.practice}` };
};

// Generic IT Security email for other IT breach types
export const generateITSecurityEmail = (data, subType) => {
  const subTypeLabels = {
    'DataBreach': 'Data Breach',
    'RansomwareAttack': 'Ransomware Attack',
    'CyberAttack': 'Cyber Attack',
    'SystemBreach': 'System Breach',
    'EmailBreach': 'Email Compromise',
    'EmailCompromise': 'Email Compromise',
    'DeviceBreach': 'Device Breach',
    'DeviceTheftLoss': 'Device Theft/Loss'
  };
  
  // Route to specific handlers
  if (subType === 'DataBreach') return generateDataBreachEmail(data);
  if (subType === 'RansomwareAttack') return generateRansomwareEmail(data);
  if (subType === 'CyberAttack') return generateCyberAttackEmail(data);
  if (subType === 'SystemBreach') return generateSystemBreachEmail(data);
  if (subType === 'EmailBreach' || subType === 'EmailCompromise') return generateEmailCompromiseEmail(data);
  if (subType === 'DeviceBreach' || subType === 'DeviceTheftLoss') return generateDeviceBreachEmail(data);
  
  const label = subTypeLabels[subType] || data.incidentType || 'IT Security Event';
  const affectedSystemsList = formatList(data.affectedSystems);
  const dataTypesList = formatList(data.dataTypes);
  const notificationsList = formatList(data.notificationsRequired);
  
  const html = `
    ${emailHeader(`🛡️ IT Security Event: ${label}`, '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold;">🚨 SECURITY INCIDENT ALERT</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">Priority: ${(data.priority || 'medium').toUpperCase()}</p>
      </div>
      
      ${section('Event Classification', `
        ${row('Type', label)}
        ${row('Date Discovered', formatDate(data.dateDiscovered))}
        ${row('Time Discovered', data.timeDiscovered)}
        ${row('Discovered By', data.discoveredBy)}
        ${row('Priority', data.priority)}
      `)}
      
      ${section('Affected Systems', `
        ${row('Systems', affectedSystemsList)}
        ${row('Estimated Patients Affected', data.estimatedPatientCount)}
        ${row('Data Compromised', formatYesNo(data.dataCompromised))}
        ${data.dataCompromised ? row('Data Types', dataTypesList) : ''}
      `, '#fef2f2')}
      
      ${data.incidentDescription ? section('Event Description', `
        <div style="white-space: pre-wrap;">${data.incidentDescription}</div>
      `) : ''}
      
      ${data.immediateActions ? section('Immediate Actions', `
        <div style="white-space: pre-wrap;">${data.immediateActions}</div>
      `) : ''}
      
      ${section('Status', `
        ${row('Containment Status', data.containmentStatus)}
        ${row('Notifications Required', notificationsList)}
      `)}
      
      ${section('Reporter Information', `
        ${row('Reported By', data.reportedBy)}
        ${row('Title', data.reportedByTitle)}
        ${row('Email', data.reportedByEmail)}
      `)}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `IT SECURITY EVENT - ${label.toUpperCase()}

Priority: ${(data.priority || 'medium').toUpperCase()}

EVENT CLASSIFICATION
Type: ${label}
Date Discovered: ${formatDate(data.dateDiscovered)}
Time Discovered: ${data.timeDiscovered || 'N/A'}
Discovered By: ${data.discoveredBy || 'N/A'}

AFFECTED SYSTEMS
Systems: ${affectedSystemsList}
Estimated Patients Affected: ${data.estimatedPatientCount || 'Unknown'}
Data Compromised: ${formatYesNo(data.dataCompromised)}
${data.dataCompromised ? `Data Types: ${dataTypesList}` : ''}

EVENT DESCRIPTION
${data.incidentDescription || 'N/A'}

IMMEDIATE ACTIONS
${data.immediateActions || 'N/A'}

STATUS
Containment Status: ${data.containmentStatus || 'N/A'}
Notifications Required: ${notificationsList}

REPORTER INFORMATION
Reported By: ${data.reportedBy || 'N/A'}
Title: ${data.reportedByTitle || 'N/A'}
Email: ${data.reportedByEmail || 'N/A'}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🛡️ IT Security: ${label} - ${data.practice}` };
};

// ============================================
// EMERGENCY REPORT TEMPLATE
// ============================================

export const generateEmergencyEmail = (data) => {
  const emergencies = data.emergencies || [];
  
  const html = `
    ${emailHeader('🚨 Emergency Report', '#dc2626')}
    <div style="padding: 20px;">
      <div style="background-color: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 5px; margin-bottom: 15px;">
        <p style="margin: 0; color: #dc2626; font-weight: bold; font-size: 18px;">🚨 EMERGENCY REPORTED</p>
        <p style="margin: 10px 0 0 0; color: #991b1b;">An emergency situation has been documented.</p>
      </div>
      
      ${emergencies.length > 0 ? section('Emergency Types Reported', `
        <ul style="margin: 0; padding-left: 20px;">
          ${emergencies.map(e => `<li style="margin: 5px 0; font-weight: bold; color: #dc2626;">${e}</li>`).join('')}
        </ul>
      `, '#fef2f2') : section('Emergency Types', `<p>None specified</p>`)}
      
      ${data.treatmentOptions && Object.keys(data.treatmentOptions).length > 0 ? section('Treatment Protocols Accessed', `
        <p style="margin: 5px 0;">Treatment protocol information was accessed for the selected emergencies.</p>
      `) : ''}
    </div>
    ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
  `;
  
  const text = `EMERGENCY REPORT

EMERGENCY TYPES REPORTED
${emergencies.length > 0 ? emergencies.map(e => `• ${e}`).join('\n') : 'None specified'}

${data.treatmentOptions && Object.keys(data.treatmentOptions).length > 0 ? 'Treatment protocol information was accessed for the selected emergencies.\n' : ''}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`;

  return { html, text, subject: `🚨 Emergency Report: ${emergencies.length > 0 ? emergencies.slice(0, 2).join(', ') + (emergencies.length > 2 ? '...' : '') : 'Emergency'} - ${data.practice}` };
};

// ============================================
// MAIN EXPORT - Get email template by form type
// ============================================

export const getEmailTemplate = (formType, data, options = {}) => {
  const { subType, specialCollection } = options;
  
  // HIPAA forms
  if (specialCollection === 'HIPAADisclosure') {
    return generateHIPAAEmail(data, subType);
  }
  
  // IT Security forms
  if (specialCollection === 'ITBreach') {
    return generateITSecurityEmail(data, subType);
  }
  
  // Aggressive Patient
  if (specialCollection === 'AggressivePatientInteractions') {
    return generateAggressivePatientEmail(data);
  }
  
  // Complications
  if (formType === 'Anesthesia') {
    return generateAnesthesiaEmail(data);
  }
  if (formType === 'Dental Implant') {
    return generateDentalImplantEmail(data);
  }
  if (formType === 'Dentoalveolar Surgery') {
    return generateDentoalveolarEmail(data);
  }
  if (formType === 'Needle Stick Injury') {
    return generateNeedleStickEmail(data);
  }
  
  // Complaints
  if (formType.startsWith('Complaint') || formType.includes('Complaint')) {
    return generateComplaintEmail({ ...data, complaintCategory: formType.replace('Complaint - ', '').replace('Complaint', 'General') });
  }
  
  // Equipment
  if (formType.startsWith('Equipment') || formType.includes('Equipment')) {
    return generateEquipmentEmail(data);
  }
  
  // Scheduling
  if (formType.startsWith('Scheduling') || formType.includes('Scheduling')) {
    return generateSchedulingEmail({ ...data, schedulingCategory: formType.replace('Scheduling - ', '').replace('Scheduling', 'General') });
  }
  
  // Emergency
  if (formType === 'Emergency') {
    return generateEmergencyEmail(data);
  }
  
  // Default fallback - show all non-empty fields
  const skipFields = ['organization', 'patientID', 'procedureId', 'physicianId', 'userEmail', 'practice', 'submittedAt', '_encrypted'];
  const displayFields = Object.entries(data)
    .filter(([key, value]) => {
      if (skipFields.includes(key)) return false;
      if (value === null || value === undefined || value === '' || value === 'N/A') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
    .map(([key, value]) => {
      const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      let formattedValue = value;
      if (Array.isArray(value)) formattedValue = value.join(', ');
      if (typeof value === 'object' && !Array.isArray(value)) formattedValue = JSON.stringify(value, null, 2);
      if (typeof value === 'boolean') formattedValue = value ? 'Yes' : 'No';
      return { key: formattedKey, value: formattedValue };
    });
  
  return {
    html: `
      ${emailHeader(`📋 ${formType} Form Submission`, '#4f46e5')}
      <div style="padding: 20px;">
        ${section('Form Details', `
          ${displayFields.map(f => `<p style="margin: 5px 0;"><strong>${f.key}:</strong> ${f.value}</p>`).join('')}
        `)}
      </div>
      ${emailFooter(data.practice, data.userEmail, data.submittedAt)}
    `,
    text: `${formType.toUpperCase()} FORM SUBMISSION

${displayFields.map(f => `${f.key}: ${f.value}`).join('\n')}

Practice: ${data.practice || 'N/A'}
Submitted By: ${data.userEmail || 'N/A'}
Date: ${formatDate(data.submittedAt)}`,
    subject: `📋 ${formType} Form Submission - ${data.practice}`
  };
};
